'use client'

import { Box, Flex, Grid, Text, Heading, Icon } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { VerifyFormButton } from '../atoms/VerifyFormButton'
import { VerifyFormTextarea } from '../atoms/VerifyFormTextarea'
import { VerifyFormTitle } from '../molecules/VerifyFormTitle'
import { FormProvider, useForm } from 'react-hook-form'
import { VerifyFormInput } from '../atoms/VerifyFormInput'
import { VerifyFormSelect } from '../molecules/VerifyFormSelect'
import PasswordIcon from '@/public/assets/icons/password.svg'
import KeyIcon from '@/public/assets/icons/key-2.svg'
import InvalidKeyIcon from '@/public/assets/icons/invalid-key.svg'
import { sha256 } from '@noble/hashes/sha256'
import { ed25519 } from '@noble/curves/ed25519'
import { bls12_381 } from '@noble/curves/bls12-381'
import { useEffect, useState } from 'react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

const schema = yup
  .object({
    mode: yup.string().required().label('Provider'),
    signature: yup
      .string()
      .required()
      .label('Signature')
      .when('mode', ([mode], schema) => {
        return mode === 'drand' ? schema.length(96) : schema.length(128)
      })
      .test('is-hex', '${path} is not hex', (value) => /^(0x)?[0-9a-fA-F]+$/.test(value || '')),
    vblock: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(1)
      .required()
      .label('The round number'),
  })
  .required()

export const SignatureVerify = () => {
  const { t } = useTranslation()
  const form = useForm({
    resolver: yupResolver(schema),
  })
  const [state, setState] = useState<boolean | undefined>()

  useEffect(() => {
    const { unsubscribe } = form.watch(() => {
      setState(undefined)
    })
    return () => unsubscribe()
  }, [form.watch])

  const handleClick = async () => {
    await form.trigger()
    if (!form.formState.isValid) {
      return
    }

    const signature = form.getValues('signature').replace('0x', '')
    const round = form.getValues('vblock')
    const mode = form.getValues('mode')

    const buffer = new ArrayBuffer(8)
    const view = new DataView(buffer)
    view.setBigUint64(0, BigInt(round), false)

    if (mode == 'drand') {
      try {
        const s = bls12_381.verifyShortSignature(
          signature,
          sha256(new Uint8Array(buffer)),
          '83cf0f2896adee7eb8b5f01fcad3912212c437e0073e911fb90022d3e760183c8c4b450b6a0a6c3ac6a5776a2d1064510d1fec758c921cc22b0e17e63aaf4bcb5ed66304de9cf809bd274ca73bab4af5a6e9c76a4bc09e76eae8991ef5ece45a'
        )

        setState(s)
      } catch (e) {
        setState(false)
      }

      return
    }

    if (mode == 'drng') {
      setState(
        ed25519.verify(
          Buffer.from(signature, 'hex'),
          new Uint8Array(buffer),
          Buffer.from('e642e1e3df5e8a802643a16f78b0164bdf4fff7616d1d90421fad34c6996b699', 'hex')
        )
      )
      return
    }
  }

  return (
    <Grid gap={5} gridTemplateColumns={{ base: '1fr', lg: '260px auto' }}>
      <Box p={5} rounded={'24px'} bg={'ninja.600'}>
        <FormProvider {...form}>
          <VerifyFormTitle autofillEnabled={!!form.watch('mode')} mode={form.watch('mode') || ('drand' as any)} />
          <VerifyFormSelect
            name="mode"
            options={[
              { label: 'Drand', value: 'drand' },
              { label: 'DRNG', value: 'drng' },
            ]}
            placeholder={t('verify.choose-provider')}
            label={t('verify.provider')}
          />
          <VerifyFormTextarea
            label={t('verify.signature')}
            placeholder={t('verify.enter-signature')}
            name="signature"
          />
          <VerifyFormInput
            label={form.watch('mode') === 'drng' ? t('verify.injective-block') : t('verify.drand-round')}
            placeholder={
              form.watch('mode') === 'drng' ? t('verify.enter-injective-block') : t('verify.enter-drand-round')
            }
            name="vblock"
          />
          <VerifyFormButton onClick={handleClick} />
        </FormProvider>
      </Box>
      <Box p={5} minH={'375px'} rounded={'24px'} bg={'ninja.600'}>
        <Heading fontSize={'xl'}>{t('common.result')}</Heading>
        <SignatureVerifyResult result={state} />
      </Box>
    </Grid>
  )
}

const SignatureVerifyResult = ({ result }: { result?: boolean }) => {
  const { t } = useTranslation()

  if (result === undefined) {
    return (
      <Flex gap={3} flexDir={'column'} alignItems={'center'} justifyContent={'center'} h={'calc(100% - 24px)'}>
        <Icon color={'ninja.200'} lineHeight={1} w={12} h={12} as={PasswordIcon} />
        <Text maxW={'200px'} textAlign={'center'} fontWeight={700} color={'ninja.200'}>
          {t('verify.add-randomness-info')}
        </Text>
      </Flex>
    )
  }

  if (result) {
    return (
      <Flex
        gap={3}
        color={'brand.green'}
        flexDir={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        h={'calc(100% - 24px)'}
      >
        <Icon lineHeight={1} w={12} h={12} as={KeyIcon} />
        <Text maxW={'200px'} textAlign={'center'} fontWeight={700}>
          {t('verify.signature-valid')}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex
      gap={3}
      color={'brand.error'}
      flexDir={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      h={'calc(100% - 24px)'}
    >
      <Icon lineHeight={1} w={12} h={12} as={InvalidKeyIcon} />
      <Text maxW={'200px'} textAlign={'center'} fontWeight={700}>
        {t('verify.signature-invalid')}
      </Text>
    </Flex>
  )
}
